import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getProducts, getCategories } from "@/lib/api";
import { catLabel } from "@/lib/content";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/data/site";
import ProductCard from "@/components/ProductCard";
import PageHero from "@/components/PageHero";
import Breadcrumb from "@/components/Breadcrumb";
import NewsletterSection from "@/components/NewsletterSection";
import useReveal from "@/hooks/useReveal";

export default function Boutique() {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("categorie") || "Tous";
  const setActive = (c) => setSearchParams(c === "Tous" ? {} : { categorie: c });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useReveal([products.length]);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, [reloadKey]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    getProducts(active === "Tous" ? null : active)
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [active, reloadKey]);

  const retry = () => setReloadKey((k) => k + 1);

  return (
    <>
      <PageHero
        tag={t("shop.tag")}
        title={t("shop.title")}
        subtitle={t("shop.subtitle")}
        image={IMAGES.boutiqueHero}
      />
      <Breadcrumb trail={[{ label: t("nav.home"), to: "/" }, { label: t("nav.shop") }]} />

      <section className="products-section">
        <div className="boutique-toolbar" data-testid="boutique-filters">
          {["Tous", ...categories].map((c) => (
            <button
              key={c}
              className={`filter-chip ${active === c ? "active" : ""}`}
              onClick={() => setActive(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
            >
              {catLabel(c, lang)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="loader" data-testid="boutique-loading"><div className="spinner" /></div>
        ) : error ? (
          <div className="boutique-state" data-testid="boutique-error">
            <p>Le chargement des produits a échoué. Vérifiez votre connexion et réessayez.</p>
            <button className="btn-primary" onClick={retry} data-testid="boutique-retry">Réessayer</button>
          </div>
        ) : products.length === 0 ? (
          <div className="boutique-state" data-testid="boutique-empty">
            <p>Aucun produit dans cette catégorie pour le moment.</p>
          </div>
        ) : (
          <div className="products-grid" data-testid="boutique-grid">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </section>

      <NewsletterSection />
    </>
  );
}
